/* @flow */

import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects'

import type { Action, CustomerId } from '../types'

import { sagaGet } from '../fetch'
import { apiUrlForPolls, apiUrlForFeaturedPolls } from '../urls'
import {
  BadResponseError,
  SagaMalfunctionError,
  sagaHandleError,
} from '../helpers'
import { selectCurrentUser } from '../stores/currentUser'
import { getSelectedCustomerId } from '../stores/ui/selectedCustomer'
import { getSearch } from '../stores/ui/search'
import { isAdmin } from '../stores/usersStore'

import { intoCustomersStore } from '../stores/customersStore'
import { intoPollTypesStore } from '../stores/pollTypesStore'
import { intoPollsStore } from '../stores/pollsStore'
import { getTagFilters, automatedTagSelected } from '../stores/pollFilters'

import { setIsFetchingPolls } from '../stores/pending'
import {
  selectNextUrlForUrl,
  getLibraryPollKey,
  intoCursoredUrlsStore,
} from '../stores/cursoredUrlsStore'
import {
  FETCH_FEATURED_POLLS,
  fetchFeaturedPollsSuccess,
  fetchFeaturedPollsError,
} from '../stores/polls/featured'

import { hardcodedPollTypes } from '../legacy'

import { sagaLogoutUnauthorizedUser } from './logout'

export const FETCH_POLLS_INTO_STORE: string = 'opinary/FETCH_POLLS_INTO_STORE'

export function fetchPollsIntoStore(): Action {
  return {
    type: FETCH_POLLS_INTO_STORE,
  }
}

export function* sagaFetchPollsIntoStore(url: string): Generator<*, void, *> {
  const response = yield call(sagaGet, url)
  if (response == null) throw new SagaMalfunctionError()

  if (response.status === 401 || response.status === 403) {
    return yield call(sagaLogoutUnauthorizedUser)
    // This triggers error when coming from library/all, locally shows notification, on staging and prod creates uncaught error
    // throw new UnauthorizedError()
  }
  if (response.status !== 200) throw new BadResponseError(response)

  const json = yield call([response, response.json])

  if (json == null) throw new SagaMalfunctionError()

  const { results, next } = json

  yield put(intoPollTypesStore(hardcodedPollTypes))
  yield put(
    intoCustomersStore(
      results.map(poll => ({ id: poll.customer, name: poll.customer }))
    )
  )
  yield put(intoPollsStore(results))
  yield put(intoCursoredUrlsStore('polls', next))
}

export function* getTargetCustomer(): Generator<*, ?CustomerId, *> {
  const currentUser = yield select(selectCurrentUser)

  if (!currentUser) return null

  if (isAdmin(currentUser)) {
    return yield select(getSelectedCustomerId)
  }

  return currentUser.customerId
}

export function* sagaRepeatedlyFetchPollsIntoStore(): Generator<*, void, *> {
  yield takeLatest(FETCH_POLLS_INTO_STORE, function*() {
    yield put(setIsFetchingPolls(true))
    try {
      const targetCustomer = yield getTargetCustomer()
      const search = yield select(getSearch)
      const filteringTags = yield select(getTagFilters)
      const automatedSelected = yield select(automatedTagSelected)
      const key = yield select(getLibraryPollKey)
      const nextUrl = yield select(selectNextUrlForUrl, key)

      yield sagaFetchPollsIntoStore(
        nextUrl ||
          apiUrlForPolls(
            false,
            targetCustomer,
            search,
            filteringTags,
            automatedSelected
          )
      )
    } catch (err) {
      yield call(sagaHandleError, err)
    }
    yield put(setIsFetchingPolls(false))
  })
}

export function* sagaRepeatedlyFetchFeaturedPollsIntoStore(): Generator<
  *,
  void,
  *
> {
  yield takeEvery(
    [
      // explicit fetch
      FETCH_FEATURED_POLLS,
    ],
    sagaFetchFeaturedPollsIntoStore
  )
}

export function* sagaFetchFeaturedPollsIntoStore(): Generator<*, void, *> {
  try {
    const targetCustomer = yield getTargetCustomer()
    const nextUrl = yield select(selectNextUrlForUrl, 'featured-polls')
    const response = yield call(
      sagaGet,
      nextUrl || apiUrlForFeaturedPolls(targetCustomer)
    )
    if (response == null) throw new SagaMalfunctionError()
    if (response.status === 401 || response.status === 403)
      return yield call(sagaLogoutUnauthorizedUser)
    if (response.status === 404) return
    if (response.status !== 200) throw new BadResponseError(response)

    const json = yield call([response, response.json])
    if (json == null) throw new SagaMalfunctionError()

    const { results, next } = json

    yield put(fetchFeaturedPollsSuccess(results))
    yield put(intoCursoredUrlsStore('featured-polls', next))
  } catch (error) {
    yield put(fetchFeaturedPollsError(error))
  }
}



// WEBPACK FOOTER //
// ./src/sagas/fetchPolls.js